// Admin WebSocket consumer for the EventsHub Durable Object. Mounted
// once at the app root via `useAdminEvents()`; pushes invalidations
// into React Query so live-map, issues, and orders pages all stay
// in sync with sub-second latency.
//
// Polling intervals on those pages stay in place as a reconnect-window
// fallback (a few seconds longer than today is fine — the WS is the
// fast path). Network blip → automatic exponential-backoff reconnect.

import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'

const WS_BASE_URL = (import.meta.env.VITE_API_URL ?? 'https://api.drinx.jo')
  .replace(/^http:/, 'ws:')
  .replace(/^https:/, 'wss:')

interface EventEnvelope {
  type: string
  payload?: Record<string, unknown>
}

export function useAdminEvents(token: string | null): void {
  const qc = useQueryClient()
  // Refs survive re-renders so we don't open multiple sockets and the
  // backoff state doesn't reset on every parent re-render.
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimerRef = useRef<number | null>(null)
  const heartbeatTimerRef = useRef<number | null>(null)
  const backoffSecondsRef = useRef(2)
  const stoppedRef = useRef(false)

  useEffect(() => {
    if (!token) return

    stoppedRef.current = false
    backoffSecondsRef.current = 2

    const connect = () => {
      if (stoppedRef.current) return

      // JWT goes in the query string — WebSocket upgrade can't carry
      // an Authorization header from a browser. Worker verifies the
      // same way the regular HTTP middleware does, including the
      // token_version revocation check.
      const url = `${WS_BASE_URL}/admin/events/ws?token=${encodeURIComponent(token)}`
      let ws: WebSocket
      try {
        ws = new WebSocket(url)
      } catch {
        scheduleReconnect()
        return
      }
      wsRef.current = ws

      ws.onopen = () => {
        backoffSecondsRef.current = 2
        // App-level heartbeat — DO replies with `pong`. Most CDNs +
        // browsers drop idle WebSockets after ~60s without traffic,
        // even with low-level ping frames.
        if (heartbeatTimerRef.current) window.clearInterval(heartbeatTimerRef.current)
        heartbeatTimerRef.current = window.setInterval(() => {
          try { ws.send(JSON.stringify({ type: 'ping' })) }
          catch { /* will be caught by onclose/onerror */ }
        }, 25_000)
      }

      ws.onmessage = (ev) => {
        if (typeof ev.data !== 'string') return
        let msg: EventEnvelope
        try { msg = JSON.parse(ev.data) }
        catch { return }
        handleEvent(qc, msg)
      }

      ws.onerror = () => { /* onclose follows */ }
      ws.onclose = () => {
        if (heartbeatTimerRef.current) {
          window.clearInterval(heartbeatTimerRef.current)
          heartbeatTimerRef.current = null
        }
        wsRef.current = null
        scheduleReconnect()
      }
    }

    const scheduleReconnect = () => {
      if (stoppedRef.current) return
      if (reconnectTimerRef.current) window.clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = window.setTimeout(() => {
        backoffSecondsRef.current = Math.min(backoffSecondsRef.current * 2, 30)
        connect()
      }, backoffSecondsRef.current * 1000)
    }

    connect()

    return () => {
      stoppedRef.current = true
      if (reconnectTimerRef.current) window.clearTimeout(reconnectTimerRef.current)
      if (heartbeatTimerRef.current) window.clearInterval(heartbeatTimerRef.current)
      wsRef.current?.close()
      wsRef.current = null
    }
  }, [token, qc])
}

function handleEvent(qc: ReturnType<typeof useQueryClient>, msg: EventEnvelope): void {
  switch (msg.type) {
    case 'hello':
    case 'pong':
      return
    case 'order.status_change':
    case 'order.assignment':
      // Both events shuffle the orders list + the live-map view (driver
      // disappears once delivered) + the dispatch queue.
      qc.invalidateQueries({ queryKey: ['orders'] })
      qc.invalidateQueries({ queryKey: ['admin-dispatch-live'] })
      break
    case 'dispatch.new_order':
      // New unclaimed order — admin Dispatch page + Live Map both
      // care. Orders page also gets a fresh row.
      qc.invalidateQueries({ queryKey: ['orders'] })
      qc.invalidateQueries({ queryKey: ['admin-dispatch-live'] })
      break
    case 'issue.change':
      // Either filed by a driver/customer or resolved by an admin.
      // Refresh the Issues table + the nav badge open-count.
      qc.invalidateQueries({ queryKey: ['admin-issues'] })
      qc.invalidateQueries({ queryKey: ['issues-open-count'] })
      break
    case 'driver.location':
      // Live Map driver dots — the only event where partial cache
      // updates would help (every ping touches one driver). For now
      // we just refetch the whole list because the worker already
      // keeps live-map under a single query key.
      qc.invalidateQueries({ queryKey: ['admin-dispatch-live'] })
      break
    default:
      // Unknown — silently drop. Sentry already catches genuine bugs
      // and console spam isn't useful for an unknown event type.
  }
}
